import { format } from "date-fns";

import { StatementSummary, TemplateContext } from "@/types/templates";
import { formatNumberByCommas } from "@/util/helpers";

const CURRENCY_GBP = "GBP";

export const formatStatementLines = (
  statements: Array<StatementSummary>
): Array<string> => {
  if (!statements || statements.length === 0) return [];

  return statements.map((s) => {
    const currency = s.currency || "";
    const amountFormatted =
      currency === CURRENCY_GBP
        ? `£${formatNumberByCommas(s.amount)}`
        : `${currency ? `${currency} ` : ""}${formatNumberByCommas(s.amount)}`;
    const supplier = s.supplier || s.description || "Unknown supplier";
    const dateFormatted = format(new Date(s.date), "dd-MM-yyyy");
    return `${amountFormatted} payment to ${supplier} on ${dateFormatted}`;
  });
};

const replaceCurlyBraces = (input: string, ctx: TemplateContext): string => {
  const assignee = ctx.assigneeName || "there";
  const creator = ctx.creatorName || "";

  return input
    .replace(/\{\s*(assignee|name|person\s*who\s*created\s*the\s*query)\s*\}/gi, (match) => {
      const key = match.toLowerCase();
      if (key.includes("person") || key.includes("created")) return creator;
      return assignee;
    })
    .replace(/\{\s*(creator)\s*\}/gi, creator);
};

const replaceSquareBrackets = (input: string, ctx: TemplateContext): string => {
  let output = input;

  // Scalar tokens
  if (ctx.date) {
    const formatted = format(new Date(ctx.date), "dd MMM yyyy");
    output = output.replace(/\[\s*Date\s*\]/g, formatted);
  }
  if (ctx.amount !== undefined && ctx.amount !== null) {
    const currency = ctx.currency || "";
    const formattedAmount =
      currency === CURRENCY_GBP
        ? `£${formatNumberByCommas(ctx.amount)}`
        : `${currency ? `${currency} ` : ""}${formatNumberByCommas(ctx.amount)}`;
    output = output.replace(/\[\s*Amount\s*\]/g, formattedAmount);
  }
  if (ctx.description) {
    output = output.replace(/\[\s*Current\s*Description\s*\]/g, ctx.description);
  }
  if (ctx.reference) {
    output = output.replace(
      /\[\s*Bank\s*Reference\/Description\s*\]/g,
      ctx.reference
    );
  }

  // Array-aware tokens
  const statements = ctx.statements || [];
  const detailsLines = formatStatementLines(statements);

  output = output.replace(
    /\[\s*StatementDetailsList\s*\]/g,
    detailsLines.join("\n")
  );

  output = output.replace(/\[\s*StatementCount\s*\]/g, String(statements.length || 0));

  if (statements.length > 0) {
    const dates = statements
      .map((s) => new Date(s.date))
      .filter((d) => !isNaN(d.getTime()));
    if (dates.length > 0) {
      const first = new Date(Math.min(...dates.map((d) => d.getTime())));
      const last = new Date(Math.max(...dates.map((d) => d.getTime())));
      output = output
        .replace(/\[\s*FirstStatementDate\s*\]/g, format(first, "dd MMM yyyy"))
        .replace(/\[\s*LastStatementDate\s*\]/g, format(last, "dd MMM yyyy"));
    }
  } else {
    output = output
      .replace(/\[\s*FirstStatementDate\s*\]/g, "")
      .replace(/\[\s*LastStatementDate\s*\]/g, "");
  }

  return output;
};

export const substituteTemplatePlaceholders = (
  input: string,
  ctx: TemplateContext
): string => {
  let output = input;
  output = replaceCurlyBraces(output, ctx);
  output = replaceSquareBrackets(output, ctx);
  return output;
};

export const buildTemplateContextFromEntry = (
  entry: Partial<TemplateContext>
): TemplateContext => {
  return { ...entry } as TemplateContext;
};


